import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type {
	DiagnosticsActivitySegment,
	DiagnosticsRecordingSession,
} from "@/gen/omnios/v1/diagnostics_pb";
import { diagnosticsClient, shortId } from "@/lib/diagnostics";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import {
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Brain, ExternalLink, Loader2, RotateCcw, Sparkles, Wifi } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/diagnostics/activity")({
	component: ActivityFeedPage,
});

const PAGE_SIZE = 200;

function ActivityFeedPage() {
	const { t } = useTranslation("diagnostics");
	useBreadcrumbs([
		{
			label: t("title", { defaultValue: "Диагностика" }),
			href: "/diagnostics",
		},
		{ label: t("sidebar_activity", { defaultValue: "Активность" }) },
	]);

	const [filterSession, setFilterSession] = useState<string>("");
	const [filterEmployee, setFilterEmployee] = useState<string>("");

	// Infinite-scrolled workspace-wide call. The backend caps at 2000 per
	// page so even huge workspaces page sanely. Bottom sentinel pulls the
	// next page when it scrolls into view (IntersectionObserver below).
	const segmentsQuery = useInfiniteQuery({
		queryKey: ["diagnostics", "workspace-activity"],
		queryFn: ({ pageParam }) =>
			diagnosticsClient.listWorkspaceActivity({
				page: { pageSize: PAGE_SIZE, pageToken: pageParam ?? "" },
			}),
		initialPageParam: "" as string,
		getNextPageParam: (last) => last.page?.nextPageToken || undefined,
		refetchInterval: 60_000,
	});

	const allSegments = useMemo(
		() => segmentsQuery.data?.pages.flatMap((p) => p.segments) ?? [],
		[segmentsQuery.data],
	);

	const sessionsQuery = useQuery({
		queryKey: ["diagnostics", "sessions", "activity-feed"],
		queryFn: () =>
			diagnosticsClient.listRecordingSessions({
				page: { pageSize: 100, pageToken: "" },
			}),
	});

	const sessions = sessionsQuery.data?.sessions ?? [];
	const sessionsById = useMemo(() => {
		const m = new Map<string, DiagnosticsRecordingSession>();
		for (const s of sessions) m.set(s.id, s);
		return m;
	}, [sessions]);

	// Sessions that pass the employee filter (or all sessions if no filter).
	const sessionsAllowedByEmployee = useMemo(() => {
		if (!filterEmployee) return null; // null = no filter
		const set = new Set<string>();
		for (const s of sessions) {
			if (s.employeeId === filterEmployee) set.add(s.id);
		}
		return set;
	}, [filterEmployee, sessions]);

	const grouped = useMemo(() => {
		const buckets = new Map<string, DiagnosticsActivitySegment[]>();
		for (const seg of allSegments) {
			if (filterSession && seg.recordingSessionId !== filterSession) continue;
			if (
				sessionsAllowedByEmployee &&
				!sessionsAllowedByEmployee.has(seg.recordingSessionId)
			)
				continue;
			const list = buckets.get(seg.recordingSessionId);
			if (list) list.push(seg);
			else buckets.set(seg.recordingSessionId, [seg]);
		}
		for (const list of buckets.values()) {
			list.sort((a, b) => {
				const at = a.startedAt?.toDate()?.getTime() ?? 0;
				const bt = b.startedAt?.toDate()?.getTime() ?? 0;
				return at - bt;
			});
		}
		return Array.from(buckets.entries());
	}, [allSegments, filterSession, sessionsAllowedByEmployee]);

	const employeeOptions = useMemo(() => {
		const ids = new Set<string>();
		for (const s of sessions) if (s.employeeId) ids.add(s.employeeId);
		return Array.from(ids).sort();
	}, [sessions]);

	// IntersectionObserver-driven load-more. The sentinel sits at the
	// bottom of the scroll container; when it enters the viewport we ask
	// TanStack Query for the next page. `hasNextPage` flips to false
	// when the backend returns an empty nextPageToken.
	const sentinelRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return;
		const io = new IntersectionObserver(
			(entries) => {
				if (
					entries[0]?.isIntersecting &&
					segmentsQuery.hasNextPage &&
					!segmentsQuery.isFetchingNextPage
				) {
					segmentsQuery.fetchNextPage();
				}
			},
			{ rootMargin: "200px" },
		);
		io.observe(el);
		return () => io.disconnect();
	}, [segmentsQuery]);

	return (
		<div className="flex flex-col h-full">
			<div className="px-4 py-3 border-b flex flex-col gap-1">
				<div className="flex items-center gap-2">
					<Brain className="h-5 w-5 text-muted-foreground" />
					<h1 className="text-base font-semibold">
						{t("activity_feed_title")}
					</h1>
				</div>
				<p className="text-xs text-muted-foreground">
					{t("activity_feed_subtitle")}
				</p>
			</div>

			<div className="px-4 py-2 border-b flex items-center gap-3 text-xs">
				<div className="flex items-center gap-1.5">
					<label className="text-muted-foreground">
						{t("activity_feed_filter_employee", { defaultValue: "Сотрудник" })}
					</label>
					<select
						value={filterEmployee}
						onChange={(e) => setFilterEmployee(e.target.value)}
						className="border rounded px-2 py-1 text-xs bg-background"
					>
						<option value="">
							{t("activity_feed_filter_all_employees", {
								defaultValue: "Все сотрудники",
							})}
						</option>
						{employeeOptions.map((id) => (
							<option key={id} value={id}>
								{shortId(id)}
							</option>
						))}
					</select>
				</div>
				<div className="flex items-center gap-1.5">
					<label className="text-muted-foreground">
						{t("activity_feed_filter_session")}
					</label>
					<select
						value={filterSession}
						onChange={(e) => setFilterSession(e.target.value)}
						className="border rounded px-2 py-1 text-xs bg-background"
					>
						<option value="">
							{t("activity_feed_filter_all_sessions")}
						</option>
						{sessions
							.filter(
								(s) => !filterEmployee || s.employeeId === filterEmployee,
							)
							.map((s) => (
								<option key={s.id} value={s.id}>
									{shortId(s.id)} —{" "}
									{s.employeeId ? shortId(s.employeeId) : "—"}
								</option>
							))}
					</select>
				</div>
			</div>

			<div className="flex-1 overflow-y-auto">
				{segmentsQuery.isLoading ? (
					<div className="p-4 space-y-2">
						<Skeleton className="h-16 w-full" />
						<Skeleton className="h-16 w-full" />
					</div>
				) : grouped.length === 0 ? (
					<div className="flex-1 flex flex-col items-center justify-center gap-2 text-center py-12">
						<Brain className="h-10 w-10 text-muted-foreground/40" />
						<p className="text-sm text-muted-foreground">
							{t("activity_feed_empty")}
						</p>
					</div>
				) : (
					<>
						{grouped.map(([sessionId, segs]) => (
							<SessionActivityCard
								key={sessionId}
								sessionId={sessionId}
								session={sessionsById.get(sessionId)}
								segments={segs}
							/>
						))}
						{/* Load-more sentinel + status row */}
						<div
							ref={sentinelRef}
							className="px-4 py-4 flex items-center justify-center text-[11px] text-muted-foreground"
						>
							{segmentsQuery.isFetchingNextPage ? (
								<span className="inline-flex items-center gap-1.5">
									<Loader2 className="h-3 w-3 animate-spin" />
									{t("activity_feed_loading_more", {
										defaultValue: "Загрузка…",
									})}
								</span>
							) : segmentsQuery.hasNextPage ? (
								<button
									type="button"
									onClick={() => segmentsQuery.fetchNextPage()}
									className="text-xs hover:text-foreground"
								>
									{t("activity_feed_load_more", {
										defaultValue: "Загрузить ещё",
									})}
								</button>
							) : (
								<span>
									{t("activity_feed_end", { defaultValue: "Это всё" })}
								</span>
							)}
						</div>
					</>
				)}
			</div>
		</div>
	);
}

function SessionActivityCard({
	sessionId,
	session,
	segments,
}: {
	sessionId: string;
	session: DiagnosticsRecordingSession | undefined;
	segments: DiagnosticsActivitySegment[];
}) {
	const { t } = useTranslation("diagnostics");
	const qc = useQueryClient();
	const openCount = useMemo(
		() => segments.filter((s) => s.isOpen).length,
		[segments],
	);

	const reaggregate = useMutation({
		mutationFn: () =>
			diagnosticsClient.reaggregateSession({ recordingSessionId: sessionId }),
		onSuccess: async (r) => {
			await qc.invalidateQueries({
				queryKey: ["diagnostics", "workspace-activity"],
			});
			toast.success(
				t("activity_feed_reaggregated", { count: r.segmentsCreated }),
			);
		},
		onError: (e) => toast.error(String(e)),
	});

	const reset = useMutation({
		mutationFn: () =>
			diagnosticsClient.resetSegmentationCursor({
				recordingSessionId: sessionId,
				rewind: false,
			}),
		onSuccess: () =>
			toast.success(
				t("activity_feed_cursor_reset", { defaultValue: "Курсор сброшен" }),
			),
		onError: (e) => toast.error(String(e)),
	});

	const busy = reaggregate.isPending || reset.isPending;

	const onReaggregate = () => {
		// Destructive: wipes existing segments. Confirm before firing.
		const ok = window.confirm(
			t("activity_feed_reaggregate_confirm", {
				defaultValue:
					"Пересегментировать сессию? Существующие блоки будут пересозданы.",
			}),
		);
		if (ok) reaggregate.mutate();
	};

	return (
		<div className="border-b">
			<div className="px-4 py-2 bg-muted/30 flex items-center gap-2">
				<span className="text-xs font-medium">{shortId(sessionId)}</span>
				<span className="text-[10px] text-muted-foreground">
					{session?.employeeId ? shortId(session.employeeId) : "—"}
				</span>
				<span className="text-[10px] text-muted-foreground tabular-nums ml-2">
					{segments.length} блоков
				</span>
				{openCount > 0 && (
					<span className="text-[9px] uppercase tracking-wider bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full flex items-center gap-1">
						<Wifi className="h-2.5 w-2.5" />
						{t("activity_open_badge")}
					</span>
				)}
				<div className="ml-auto flex items-center gap-1">
					<Link
						to="/diagnostics/session/$id"
						params={{ id: sessionId }}
						className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded hover:bg-muted"
						title={t("activity_feed_jump_to_session")}
					>
						<ExternalLink className="h-3 w-3" />
					</Link>
					<Button
						variant="ghost"
						size="sm"
						className="h-6 text-[10px] px-2"
						onClick={onReaggregate}
						disabled={busy}
					>
						<Sparkles className="h-3 w-3 mr-1" />
						{t("activity_feed_reaggregate")}
					</Button>
					<Button
						variant="ghost"
						size="sm"
						className="h-6 text-[10px] px-2"
						onClick={() => reset.mutate()}
						disabled={busy}
					>
						<RotateCcw className="h-3 w-3 mr-1" />
						{t("activity_feed_reset_cursor")}
					</Button>
				</div>
			</div>
			<div>
				{segments.map((seg) => (
					<SegmentRow key={seg.id} segment={seg} />
				))}
			</div>
		</div>
	);
}

function SegmentRow({ segment }: { segment: DiagnosticsActivitySegment }) {
	const start = segment.startedAt?.toDate();
	const end = segment.completedAt?.toDate();
	return (
		<div className="px-4 py-1.5 flex items-start gap-3 hover:bg-muted/30">
			<Sparkles className="h-3 w-3 text-muted-foreground mt-0.5 shrink-0" />
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2">
					<span className="text-xs font-medium truncate">
						{segment.label || "—"}
					</span>
					<span className="text-[10px] text-muted-foreground tabular-nums">
						{start ? start.toLocaleTimeString() : "—"}
						{end ? ` — ${end.toLocaleTimeString()}` : ""}
					</span>
				</div>
				{segment.summary && (
					<p className="text-[11px] text-muted-foreground line-clamp-1">
						{segment.summary}
					</p>
				)}
			</div>
			<span className="text-[10px] text-muted-foreground/70 tabular-nums shrink-0">
				{segment.observationCount}
			</span>
		</div>
	);
}
